import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useChatMessages } from "@/hooks/useChatMessages";
import { useReadReceipts } from "@/hooks/useReadReceipts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Network, Users, Settings, LogIn } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { formatDateWithTimezone, getDateKeyWithTimezone } from "@/utils/timezoneUtils";

import ConversationDateHeader from "@/components/ConversationDateHeader";
import ChatMessage from "@/components/ChatMessage";
import DiscordLikeInput from "@/components/DiscordLikeInput";
import EnhancedChatRoomMembers from "@/components/EnhancedChatRoomMembers";
import ThreadView from "@/components/ThreadView";
import TypingIndicator from "@/components/TypingIndicator";
import ChatRoomSettings from "@/components/ChatRoomSettings";
import UserSearchModal from "@/components/UserSearchModal";

type ChatRoom = Database['public']['Tables']['chat_rooms']['Row'];
type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];

// Extended chat message type that includes reply_to data
interface ChatMessageWithReply extends ChatMessage {
  reply_to?: {
    id: string;
    content: string;
    sender_id: string;
    media_type?: string;
    media_url?: string;
    sent_at: string;
  };
}

export default function ChatRoomView() {
  const { roomId } = useParams<{ roomId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [selectedThread, setSelectedThread] = useState<ChatMessageWithReply | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessageWithReply | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [canDeleteOthersMessages, setCanDeleteOthersMessages] = useState(false);

  const {
    messages,
    loading: messagesLoading,
    sendMessage,
    editMessage,
    deleteMessage,
    addSticker,
    removeSticker,
    messageStickers,
    getUserDisplayName,
    getUserAvatarUrl
  } = useChatMessages(roomId || '');

  // Show all messages in chat (including replies inline)
  const chatMessages = messages as ChatMessageWithReply[];

  // Get message IDs for read receipts  
  const messageIds = chatMessages.map(msg => msg.id);
  const { readReceipts, markAsRead, markMultipleAsRead } = useReadReceipts(messageIds, 'chat_room');

  // Group messages by date for conversation headers (using timezone-aware date keys)
  const messagesByDate = chatMessages.reduce((acc, message) => {
    const messageDate = getDateKeyWithTimezone(message.sent_at || '');
    if (!acc[messageDate]) {
      acc[messageDate] = [];
    }
    acc[messageDate].push(message);
    return acc;
  }, {} as Record<string, typeof chatMessages>);

  // Fetch user role and permissions
  useEffect(() => {
    if (!roomId || !user || !room) return;

    const fetchUserRole = async () => {
      try {
        // Check if user is room creator
        const isCreator = room.created_by === user.id;
        
        if (isCreator) {
          setCurrentUserRole('creator');
          setCanDeleteOthersMessages(true);
          return;
        }

        // For private rooms, check membership role
        if (room.is_private) {
          const { data: membership } = await supabase
            .from('chat_room_members')
            .select('role')
            .eq('room_id', roomId)
            .eq('user_id', user.id)
            .single();

          if (membership) {
            setCurrentUserRole(membership.role);
            setCanDeleteOthersMessages(['admin', 'moderator'].includes(membership.role));
          }
        } else {
          // For public rooms, regular members can't delete others' messages
          setCurrentUserRole('member');
          setCanDeleteOthersMessages(false);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setCurrentUserRole('member');
        setCanDeleteOthersMessages(false);
      }
    };

    fetchUserRole();
  }, [roomId, user, room]);

  // Fetch room data
  useEffect(() => {
    if (!roomId) return;

    const fetchRoom = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("chat_rooms")
          .select("*")
          .eq("id", roomId)
          .single();

        if (error) {
          console.error('Chat room fetch error:', error);
          const raw = JSON.stringify(error || {}).toLowerCase();
          if (raw.includes("unauthorized") || raw.includes("permission") || raw.includes("row level security") || raw.includes("rls") || raw.includes("not allowed")) {
            setAccessDenied(true);
          } else {
            setError(error.message);
          }
          return;
        }

        setRoom(data);
      } catch (err) {
        setError('Failed to fetch chat room');
        console.error('Error fetching chat room:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoom();
  }, [roomId]);

  // Mark messages as read when user views the room
  useEffect(() => {
    if (!user || messageIds.length === 0) return;
    
    // Mark all visible messages as read after a short delay
    const timer = setTimeout(() => {
      const unreadMessageIds = messageIds.filter(id => {
        const message = messages.find(m => m.id === id);
        return message && message.sender_id !== user.id;
      });
      
      if (unreadMessageIds.length > 0) {
        markMultipleAsRead(unreadMessageIds);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [messageIds, user, markMultipleAsRead, messages]);

  // Auto-scroll functionality
  const scrollToBottom = (behavior: 'smooth' | 'instant' = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Scroll to bottom when messages change or stickers are added (unless user has scrolled up)
  useEffect(() => {
    if (!userHasScrolled && (chatMessages.length > 0 || messageStickers.size > 0)) {
      // Small delay to ensure DOM is updated with new stickers
      setTimeout(() => scrollToBottom('smooth'), 100);
    }
  }, [chatMessages.length, messageStickers.size, userHasScrolled]);

  // Enhanced scroll effect for media messages with better timing
  useEffect(() => {
    if (!userHasScrolled && chatMessages.length > 0) {
      const latestMessage = chatMessages[chatMessages.length - 1];
      // If the latest message has media, ensure we scroll with multiple attempts for proper loading
      if (latestMessage?.media_type && latestMessage?.media_url) {
        console.log('📱 Media message detected, ensuring complete scroll to bottom');
        
        // Multiple scroll attempts with increasing delays to account for media loading
        setTimeout(() => scrollToBottom('smooth'), 100);  // First attempt
        setTimeout(() => scrollToBottom('smooth'), 500);  // Second attempt after media starts loading
        setTimeout(() => scrollToBottom('smooth'), 1000); // Final attempt after media fully loads
      }
    }
  }, [chatMessages, userHasScrolled]);

  // Scroll to bottom immediately when room loads
  useEffect(() => {
    if (room && chatMessages.length > 0 && !messagesLoading) {
      setTimeout(() => {
        scrollToBottom('instant');
        setUserHasScrolled(false); // Reset scroll state
      }, 200);
    }
  }, [room, messagesLoading]);

  // Handle scroll detection
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    
    // If user scrolls near bottom, reset the scroll flag
    if (isNearBottom) {
      setUserHasScrolled(false);
    } else if (!userHasScrolled) {
      // Only set if not already set to avoid unnecessary re-renders
      setUserHasScrolled(true);
    }
  };

  const handleUserAdded = () => {
    // Refresh or handle user addition
    console.log("User added to room");
  };

  const handleReply = (message: ChatMessageWithReply) => {
    setReplyingTo(message);
    // Don't open thread view for replies, keep in main chat
  };

  const handleClearReply = () => {
    setReplyingTo(null);
  };

  const closeThread = () => {
    setSelectedThread(null);
  };

  const handleAddSticker = async (messageId: string, emoji: string) => {
    console.log("ChatRoomView - handleAddSticker called:", { messageId, emoji });
    await addSticker(messageId, emoji);
  };

  const handleRemoveSticker = async (messageId: string, emoji: string) => {
    console.log("ChatRoomView - handleRemoveSticker called:", { messageId, emoji });
    await removeSticker(messageId, emoji);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
            <p className="text-white text-lg font-medium">Loading chat room...</p>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md mx-auto">
              <p className="text-white text-lg mb-4">You don't have access to this private chat room.</p>
              <Button variant="outline" onClick={() => window.history.back()} className="text-white border-white hover:bg-white hover:text-primary">
                Go back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md mx-auto">
              <p className="text-white text-lg">Error: {error || "Chat room not found"}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange relative overflow-hidden flex flex-col">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -left-4 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-4 w-96 h-96 bg-anyskill-orange/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-4 left-1/3 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 flex-1 flex flex-col min-h-0 container mx-auto px-4 py-6 mt-[100px]">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 shadow-2xl flex-1 flex flex-col min-h-0 max-w-5xl mx-auto w-full max-h-[75vh]">
          <div className="bg-card rounded-xl border-0 flex flex-1 min-h-0 overflow-hidden">
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Header - Fixed */}
              <CardHeader className="border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Back Arrow */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/chat-rooms')}
                      className="hover:bg-muted/50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    
                    <Avatar className="h-12 w-12 ring-2 ring-anyskill-purple/20">
                      <AvatarFallback className="bg-gradient-to-br from-anyskill-purple to-anyskill-blue text-white font-bold text-lg">
                        {room.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-xl font-bold bg-gradient-to-r from-anyskill-purple to-anyskill-blue bg-clip-text text-transparent">
                        {room.name}
                      </CardTitle>
                      {room.description && (
                        <p className="text-muted-foreground text-sm mt-1">
                          {room.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        {room.is_private && (
                          <Badge className="bg-gradient-to-r from-anyskill-purple to-anyskill-blue text-white border-0 text-xs">
                            Private ✨
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Created {formatDateWithTimezone(room.created_at || '', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex items-center space-x-2">
                    {/* Members button - Icon only */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMembers(!showMembers)}
                      className="h-9 w-9 p-0"
                      title={showMembers ? 'Hide Members' : 'Show Members'}
                    >
                      <Users className="h-4 w-4" />
                    </Button>

                    {/* Room Management - Only for room creator */}
                    {user?.id === room.created_by && (
                      <ChatRoomSettings 
                        room={room} 
                        onRoomUpdate={(updatedRoom) => {
                          setRoom({
                            ...room,
                            ...updatedRoom,
                            description: updatedRoom.description || room.description || ''
                          });
                        }}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 w-9 p-0"
                          title="Room Management"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </ChatRoomSettings>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {/* Messages Area - Flexible with proper scrolling */}
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <ScrollArea className="flex-1 px-4 py-2 h-0" onScroll={handleScroll}>
                  <div className="min-h-full flex flex-col justify-end">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-anyskill-purple"></div>
                        <p className="text-muted-foreground ml-3">Loading messages...</p>
                      </div>
                    ) : chatMessages.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <Network className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                          <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                            No messages yet
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            Be the first to start the conversation!
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 pb-4">
                        {Object.keys(messagesByDate)
                          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                          .map((dateKey) => (
                            <div key={dateKey}>
                              {/* Conversation Date Header */}
                              <ConversationDateHeader date={messagesByDate[dateKey][0].sent_at || ''} />
                              
                              {/* Messages for this date */}
                              {messagesByDate[dateKey].map((message) => (
                                <ChatMessage
                                  key={message.id}
                                  message={message}
                                  isCurrentUser={message.sender_id === user?.id}
                                  senderName={message.sender_id ? getUserDisplayName(message.sender_id) : undefined}
                                  avatarUrl={message.sender_id ? getUserAvatarUrl(message.sender_id) : undefined}
                                  repliedToSenderName={
                                    message.reply_to?.sender_id 
                                      ? getUserDisplayName(message.reply_to.sender_id) 
                                      : undefined
                                  }
                                  onEdit={editMessage}
                                  onDelete={deleteMessage}
                                  onReply={handleReply}
                                  onAddSticker={handleAddSticker}
                                  onRemoveSticker={handleRemoveSticker}
                                  messageStickers={messageStickers.get(message.id) || []}
                                  readReceipts={readReceipts[message.id] || []}
                                  getUserDisplayName={getUserDisplayName}
                                  canDeleteOthersMessages={canDeleteOthersMessages}
                                />
                              ))}
                            </div>
                           ))}
                       </div>
                     )}
                     {/* Invisible element to scroll to */}
                     <div ref={messagesEndRef} />
                   </div>
                  </ScrollArea>
                 
                  {/* Typing Indicator */}
                  <TypingIndicator 
                    roomId={roomId!} 
                    userProfiles={new Map()} // Add user profiles map if needed
                  />
                 
                  {/* Discord-like Input - Fixed at bottom */}
                  <div className="flex-shrink-0">
                    {user ? (
                      <DiscordLikeInput 
                        onSendMessage={sendMessage} 
                        placeholder={`Message #${room.name}`}
                        roomId={roomId}
                        replyTo={replyingTo}
                        onClearReply={handleClearReply}
                        senderName={replyingTo ? getUserDisplayName(replyingTo.sender_id || '') : undefined}
                      />
                    ) : (
                      <div className="p-4 border-t bg-muted/50">
                        <div className="flex items-center justify-center gap-3 py-3">
                          <LogIn className="h-5 w-5 text-muted-foreground" />
                          <p className="text-muted-foreground text-sm">
                            <Link 
                              to="/auth?mode=signup" 
                              className="text-primary font-medium hover:underline"
                            >
                              Create an account
                            </Link>
                            {" "}or{" "}
                            <Link 
                              to="/auth?mode=login" 
                              className="text-primary font-medium hover:underline"
                            >
                              sign in
                            </Link>
                            {" "}to join the conversation
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
              </div>
            </div>

            {/* Members Sidebar */}
            {showMembers && (
              <div className="w-80 border-l flex-shrink-0 bg-muted/30">
                <EnhancedChatRoomMembers 
                  roomId={roomId!}
                  roomCreatedBy={room.created_by}
                  isPrivate={room.is_private}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Thread View with full functionality */}
      {selectedThread && (
        <ThreadView
          parentMessage={selectedThread}
          isOpen={!!selectedThread}
          onClose={closeThread}
          getUserDisplayName={getUserDisplayName}
          onEdit={editMessage}
          onDelete={deleteMessage}
          onAddSticker={handleAddSticker}
          onRemoveSticker={handleRemoveSticker}
          messageStickers={messageStickers}
          canDeleteOthersMessages={canDeleteOthersMessages}
        />
      )}
    </div>
  );
}
